import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, finalize, map, Observable, throwError } from 'rxjs';
import { storage } from 'src/environments/environment';
import { StaticService } from 'src/static.service';
import { AuthHttpService } from './auth-http/auth-http.service';
import { ResponseModel } from './model/auth.model';
import { ResetModel } from './reset-password/reset-password.component';
import { ResetEmailModel } from './model/auth.model';
import { TokenStorageService } from './token-storage.service';

export interface LoginResponseData {
  token: { access: string; refresh?: string };
  [key: string]: unknown;
}

export interface RefreshTokenData {
  access: string;
  refresh: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private staticService: StaticService,
    private authHttp: AuthHttpService,
    private tokenStorageService: TokenStorageService,
    private router: Router
  ) {}

  login(email: string, password: string): Observable<LoginResponseData | undefined> {
    this.isLoadingSubject?.next(true);
    return this.authHttp.login(email, password).pipe(
      map((res: ResponseModel<LoginResponseData>) => {
        if (this.setAuthFromLocalStorage(res.data)) {
          return res.data;
        }
        return undefined;
      }),
      catchError((err) => {
        return throwError(() => {
          return err.error;
        });
      }),
      finalize(() => this.isLoadingSubject?.next(false))
    );
  }
  setAuthFromLocalStorage(auth: unknown): boolean {
    const authObj = auth as { token?: { access?: string; refresh?: string } } | null;
    if (!authObj || !authObj.token || !authObj.token.access) {
      console.error('Invalid auth missing token');
      return false;
    }

    try {
      this.tokenStorageService.saveTokens(authObj.token.access, authObj.token.refresh ?? '');

      const decodedToken = this.tokenStorageService.decodeAccessToken();

      if (!decodedToken) {
        return false;
      }

      const name =
        decodedToken.name ||
        `${decodedToken.first_name || ''} ${decodedToken.last_name || ''}`.trim();
      if (name) {
        localStorage.setItem(storage.name, name);
      }

      if (decodedToken.role) {
        localStorage.setItem(storage.role, decodedToken.role);
      }

      if (decodedToken.tenant_id) {
        localStorage.setItem(storage.tenant_id, decodedToken.tenant_id);
      }

      if (decodedToken.tenant_name) {
        localStorage.setItem('TENANT_NAME', decodedToken.tenant_name);
      }

      if (decodedToken.tenant_logo) {
        localStorage.setItem('TENANT_LOGO', decodedToken.tenant_logo);
      }

      if (decodedToken.user_id) {
        localStorage.setItem('USER_ID', decodedToken.user_id);
      }

      return true;
    } catch {
      return false;
    }
  }
  isLoggedIn(): boolean {
    return !!this.tokenStorageService.getAccessToken();
  }

  refresh(): Observable<ResponseModel<RefreshTokenData>> {
    const refreshToken = this.tokenStorageService.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }
    return this.authHttp.refresh(refreshToken) as Observable<ResponseModel<RefreshTokenData>>;
  }

  logout() {
    this.http
      .post<ResponseModel>(this.staticService.auth.logout, {
        refresh: localStorage.getItem(storage.refresh)!,
      })
      .subscribe({
        next: () => {
          localStorage.clear();
          this.router.navigate(['/login'], {
            queryParams: {},
          });
        },
      });
  }
  forgetPassword(email: string) {
    return this.authHttp.forgetPassword(email);
  }
  resetPassword(resetModel: ResetModel) {
    return this.authHttp.reset(resetModel);
  }
  resetEmail(resetEmailModel: ResetEmailModel) {
    return this.authHttp.resetEmail(resetEmailModel);
  }
}
